import React, { useState } from 'react';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, Target, Award, ArrowRight, CheckCircle2 } from 'lucide-react';

const LoginPage = () => {
  const { login } = useAuth();
  const [phone, setPhone] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSignup, setIsSignup] = useState(false);

  const handleGoogleLogin = () => {
    if (isSignup) {
      // Store signup data for after OAuth
      sessionStorage.setItem('signup_data', JSON.stringify({
        phone: phone,
        marketing_consent: marketingConsent
      }));
    }
    login();
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800/90 to-olive-900/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/20 rounded-full border border-gold-500/30 mb-6">
                <Shield className="w-4 h-4 text-gold-400" />
                <span className="text-gold-400 text-sm font-medium">OLQ Habit Tracker</span>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                Build Your <span className="text-gold-400">Officer Like Qualities</span>
              </h1>
              
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                Track your daily habits aligned with the 15 Officer Like Qualities assessed in SSB. 
                Earn points and unlock discounts on our 90-day programs!
              </p>
              
              <div className="space-y-4">
                {[
                  'Track 15 Officer Like Qualities daily',
                  'Activity-based habit tracking',
                  'Earn 2 points for completing all habits daily',
                  '15% discount at 50 points on 90-day programs'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold-400" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Login Card */}
            <Card className="bg-white border-0 shadow-2xl">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-navy-900 flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-gold-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-navy-900 mb-2">
                    {isSignup ? 'Create Account' : 'Welcome Back'}
                  </h2>
                  <p className="text-gray-600">
                    {isSignup ? 'Start your OLQ journey today' : 'Continue your OLQ journey'}
                  </p>
                </div>
                
                {isSignup && (
                  <div className="space-y-4 mb-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-navy-900">Phone Number (Optional)</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91 98765 43210"
                        className="border-gray-200 focus:border-gold-500 focus:ring-gold-500"
                      />
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="marketing"
                        checked={marketingConsent}
                        onCheckedChange={setMarketingConsent}
                        className="mt-1"
                      />
                      <Label htmlFor="marketing" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                        I agree to receive promotional updates and consultation offers from Jay Defence Academy via email, SMS, or phone call.
                      </Label>
                    </div>
                  </div>
                )}
                
                <Button 
                  onClick={handleGoogleLogin}
                  className="w-full bg-navy-900 hover:bg-navy-800 text-white font-semibold py-6 text-lg transition-all duration-300"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                <div className="mt-6 text-center">
                  <button
                    onClick={() => setIsSignup(!isSignup)}
                    className="text-olive-600 hover:text-olive-700 font-medium transition-colors"
                  >
                    {isSignup ? 'Already have an account? Login' : "Don't have an account? Sign up"}
                  </button>
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4 text-gold-500" />
                      <span>Free to start</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Shield className="w-4 h-4 text-olive-600" />
                      <span>Secure login</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LoginPage;
